import React, { useEffect } from 'react';
import {
  Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  Typography, Box, Divider, Chip,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import GavelIcon from '@mui/icons-material/Gavel';
import CodeIcon from '@mui/icons-material/Code';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CampaignIcon from '@mui/icons-material/Campaign';
import HandshakeIcon from '@mui/icons-material/Handshake';
import PeopleIcon from '@mui/icons-material/People';
import WorkIcon from '@mui/icons-material/Work';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import SearchIcon from '@mui/icons-material/Search';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BarChartIcon from '@mui/icons-material/BarChart';
import HistoryIcon from '@mui/icons-material/History';
import DescriptionIcon from '@mui/icons-material/Description';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import BuildIcon from '@mui/icons-material/Build';
import CreateIcon from '@mui/icons-material/Create';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SecurityIcon from '@mui/icons-material/Security';
import { useLocation } from 'react-router-dom';
import { useDms } from '../contexts/DmsContext';
import useDmsPalette from '../hooks/useDmsPalette';
import { isDmsAdmin, canAccessManualEditors, canAccessDocumentEditor } from '../utils/dmsPermissions';

const VAULTS = [
  { id: 'procedures', label: 'Procedures', icon: <FolderIcon /> },
  { id: 'legal', label: 'Legal', icon: <GavelIcon /> },
  { id: 'technical', label: 'Technical', icon: <CodeIcon /> },
  { id: 'financial', label: 'Financial', icon: <AccountBalanceIcon /> },
  { id: 'marketing', label: 'Marketing', icon: <CampaignIcon /> },
  { id: 'contracts', label: 'Contracts', icon: <HandshakeIcon /> },
  { id: 'customer', label: 'Customer', icon: <PeopleIcon /> },
  { id: 'hr', label: 'HR', icon: <WorkIcon /> },
  { id: 'compliance', label: 'Compliance', icon: <VerifiedUserIcon /> },
];

const NAV_ITEMS = [
  { id: 'dashboard', path: '/', label: 'Dashboard', icon: <DashboardIcon /> },
  { id: 'search', path: '/search', label: 'AI Search', icon: <SearchIcon /> },
  { id: 'customer', path: '/customer', label: 'My Documents', icon: <PeopleIcon /> },
  { id: 'workflows', path: '/workflows', label: 'Workflows', icon: <AssignmentIcon /> },
  { id: 'templates', path: '/templates', label: 'Templates', icon: <DescriptionIcon /> },
  { id: 'editor', path: '/editor', label: 'Document Editor', icon: <CreateIcon />, userEditor: true },
  { id: 'analytics', path: '/analytics', label: 'Analytics', icon: <BarChartIcon />, adminOnly: true },
  { id: 'audit', path: '/audit', label: 'Audit Log', icon: <HistoryIcon />, adminOnly: true },
  { id: 'compliance', path: '/compliance', label: 'Compliance', icon: <SecurityIcon />, adminOnly: true },
  { id: 'manual', path: '/manual', label: 'Manual Editor', icon: <MenuBookIcon />, editorOnly: true },
  { id: 'tech-manual', path: '/tech-manual', label: 'Technical Docs', icon: <BuildIcon />, editorOnly: true },
];

function canSeeNavItem(item, user) {
  if (item.editorOnly) return canAccessManualEditors(user);
  if (item.userEditor) return canAccessDocumentEditor(user);
  if (item.adminOnly) return isDmsAdmin(user);
  return true;
}

export default function DmsSidebar({ user, onNavigate, mobileOpen, onMobileClose, isMobile }) {
  const MP = useDmsPalette();
  const location = useLocation();
  const { selectedVault, setSelectedVault, setSelectedFolder, stats, loadStats } = useDms();

  // Load stats if not yet fetched (sidebar is always mounted)
  useEffect(() => {
    if (!stats) loadStats();
  }, [stats, loadStats]);

  const getCount = (vault) => {
    if (!stats?.byVault) return 0;
    const entry = stats.byVault.find(v => v._id === vault);
    return entry?.count || 0;
  };

  const handleNavClick = (callback) => {
    callback();
    if (isMobile && onMobileClose) onMobileClose();
  };

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={isMobile ? mobileOpen : true}
      onClose={onMobileClose}
      ModalProps={{ keepMounted: true }}
      sx={{ width: 240, flexShrink: 0, '& .MuiDrawer-paper': { width: 240, boxSizing: 'border-box', pt: 8 } }}
    >
      <Box sx={{ px: 2, py: 1.5 }}>
        <Typography variant="overline" sx={{ color: MP.textSecondary, fontWeight: 700, fontSize: '0.65rem' }}>
          Navigation
        </Typography>
      </Box>
      <List dense>
        {NAV_ITEMS.filter((item) => canSeeNavItem(item, user)).map(item => (
          <ListItem key={item.id} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => handleNavClick(() => onNavigate?.(item.path))}
              sx={{
                mx: 1, borderRadius: 1,
                '&.Mui-selected': { bgcolor: `${MP.green}30`, color: MP.green },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36, color: location.pathname === item.path ? MP.green : MP.textSecondary }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 500 }} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider sx={{ my: 1, borderColor: MP.border }} />
      <Box sx={{ px: 2, py: 1.5 }}>
        <Typography variant="overline" sx={{ color: MP.textSecondary, fontWeight: 700, fontSize: '0.65rem' }}>
          Document Vaults
        </Typography>
      </Box>
      <List dense>
        {VAULTS.map(vault => (
          <ListItem key={vault.id} disablePadding>
            <ListItemButton
              selected={location.pathname === `/vault/${vault.id}` || (location.pathname.startsWith('/vault/') && selectedVault === vault.id)}
              onClick={() => handleNavClick(() => { setSelectedVault(vault.id); setSelectedFolder(null); onNavigate?.(`/vault/${vault.id}`); })}
              sx={{
                mx: 1, borderRadius: 1,
                '&.Mui-selected': { bgcolor: `${MP.blue}30`, color: MP.blue },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36, color: selectedVault === vault.id ? MP.blue : MP.textSecondary }}>
                {vault.icon}
              </ListItemIcon>
              <ListItemText primary={vault.label} primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 500 }} />
              <Chip label={getCount(vault.id)} size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: MP.surface }} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider sx={{ my: 1, borderColor: MP.border }} />
    </Drawer>
  );
}
